import React, { useState, useRef, useEffect } from 'react';
import './ListColorPicker.css';

// 20 predefined colors for lists
export const LIST_COLORS = [
  '#00aa9f', // Turquoise
  '#26c6b8', // Turquoise clair
  '#7dd3c0', // Menthe
  '#00745e', // Vert foncé
  '#2d8a7b', // Émeraude
  '#6b7c5e', // Olive
  '#e1723c', // Orange
  '#f5a572', // Pêche
  '#c25d3a', // Terracotta
  '#e57373', // Corail
  '#f59e0b', // Ambre
  '#d4a05a', // Miel
  '#8d7b6e', // Taupe
  '#d4c4b0', // Sable
  '#d4a5a5', // Rose poudré
  '#9e7b8a', // Mauve
  '#8b7cb3', // Violet doux
  '#b8a9d4', // Lavande
  '#5c7a8a', // Bleu ardoise
  '#3d6b7a', // Bleu canard
];

// Get a random color from the palette
export const getRandomListColor = (): string => {
  return LIST_COLORS[Math.floor(Math.random() * LIST_COLORS.length)];
};

interface ListColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  size?: 'small' | 'medium';
  className?: string;
}

const ListColorPicker: React.FC<ListColorPickerProps> = ({
  color,
  onChange,
  size = 'medium',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const nativePickerRef = useRef<HTMLInputElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleColorClick = (selectedColor: string) => {
    onChange(selectedColor);
    setIsOpen(false);
  };

  const handleCustomClick = () => {
    nativePickerRef.current?.click();
  };

  const handleNativeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`list-color-picker ${size} ${className} ${isOpen ? 'open' : ''}`}
    >
      {/* Current color button */}
      <button
        type="button"
        className="color-trigger"
        style={{ backgroundColor: color }}
        onClick={() => setIsOpen(!isOpen)}
        title="Choisir une couleur"
      />

      {/* Color palette popup */}
      {isOpen && (
        <div className="color-palette">
          <div className="color-grid">
            {LIST_COLORS.map((paletteColor) => (
              <button
                key={paletteColor}
                type="button"
                className={`color-pastille ${paletteColor === color ? 'selected' : ''}`}
                style={{ backgroundColor: paletteColor }}
                onClick={() => handleColorClick(paletteColor)}
                title={paletteColor}
              />
            ))}
            {/* Custom color option */}
            <button
              type="button"
              className="color-pastille custom-color"
              onClick={handleCustomClick}
              title="Couleur personnalisée"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </button>
          </div>
          {/* Hidden native color picker */}
          <input
            ref={nativePickerRef}
            type="color"
            value={color}
            onChange={handleNativeChange}
            className="native-color-input"
          />
        </div>
      )}
    </div>
  );
};

export default ListColorPicker;
